'use client';

import { useEffect, useState } from 'react';
import { RelayClient, RelayerTxType } from '@polymarket/builder-relayer-client';
import { BuilderConfig } from '@polymarket/builder-signing-sdk';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { createWalletClient, custom } from 'viem';
import { polygon } from 'viem/chains';

import { useAuth } from '@/store';
import { POLYGON_RPC_URL } from '@/utils/approvals.utils';

import {
  API_BASE_URL,
  API_ENDPOINTS,
  POLYMARKET_CHAIN_ID,
  POLYMARKET_RELAYER_URL,
  RELAYER_API_KEY,
  RELAYER_API_KEY_ADDRESS,
  SIGNING_SERVER_URL,
} from '@/constants';

/**
 * Hook to initialize and provide the Polymarket RelayClient
 * Integrates Privy (Viem) with the Remote Signing backend.
 */
export function usePolymarketClient() {
  const { ready, authenticated } = usePrivy();
  const { wallets } = useWallets();
  const [client, setClient] = useState<RelayClient | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const { accessToken, user } = useAuth();

  const [signer, setSigner] = useState<any>(null);
  const [lastInitializedAddress, setLastInitializedAddress] = useState<string | null>(null);

  useEffect(() => {
    async function initClient() {
      // 1. Basic Readiness Guards
      if (!ready || !authenticated || wallets.length === 0 || !accessToken) return;

      // 2. Resolve the best wallet to use.
      // SECURITY: never fall back to wallets[0] — that could be a MetaMask/external
      // wallet still connected at the browser level from a previous user's session.
      // Only trust the embedded wallet (always owned by this Privy user) or the
      // wallet matching the backend's stored walletAddress (proven linked to this user).
      const linkedWallet = wallets.find(
        (w) => w.address?.toLowerCase() === user?.walletAddress?.toLowerCase(),
      );
      const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
      const selectedWallet = linkedWallet || embeddedWallet;

      if (!selectedWallet) return;

      // 3. Skip if we've already initialized for THIS specific wallet address
      if (
        client &&
        lastInitializedAddress?.toLowerCase() === selectedWallet.address.toLowerCase()
      ) {
        return;
      }

      setIsInitializing(true);
      try {
        // 4. Ensure the wallet is on the correct network (Polygon - 137)
        if (selectedWallet.chainId !== `eip155:${polygon.id}`) {
          try {
            await selectedWallet.switchChain(polygon.id);
            return;
          } catch (switchError) {
            console.warn('[usePolymarketClient] Failed to switch network:', switchError);
            return;
          }
        }

        // 5. Convert Privy provider to Viem WalletClient
        const provider = await selectedWallet.getEthereumProvider();

        // 6. Ensure the address is available in the provider (fixes "No embedded or connected wallet found" for new users)
        let accounts: string[] = [];
        let retries = 0;
        while (retries < 10) {
          accounts = (await provider.request({ method: 'eth_accounts' })) as string[];
          if (accounts.some((a) => a.toLowerCase() === selectedWallet.address.toLowerCase())) {
            break;
          }
          await new Promise((r) => setTimeout(r, 1000));
          retries++;
        }

        const walletClient = createWalletClient({
          account: selectedWallet.address as `0x${string}`,
          chain: polygon,
          transport: custom(provider),
        });

        // Configure BuilderConfig with remote signing
        const builderConfig = new BuilderConfig({
          remoteBuilderConfig: {
            url: SIGNING_SERVER_URL,
            token: accessToken,
          },
        });

        const relayClient = new RelayClient(
          POLYMARKET_RELAYER_URL!,
          POLYMARKET_CHAIN_ID,
          walletClient as any,
          // App uses builder-signing-sdk@1.x while the relayer client declares
          // ^0.0.8 — runtime-compatible (same auth-header API), nominally not.
          builderConfig as any,
          RelayerTxType.SAFE,
          // v0.0.10 checks the deposit-wallet factory's beacon on-chain to derive
          // the correct wallet address (Polymarket migrated to beacon proxies).
          // Without this, it falls back to viem's default public Polygon RPC,
          // which is unreliable.
          {
            chain: {
              ...polygon,
              rpcUrls: { default: { http: [POLYGON_RPC_URL] } },
            },
          },
        );

        setSigner(walletClient);
        setLastInitializedAddress(selectedWallet.address);
        setClient(relayClient);
      } catch (error) {
        console.error('[usePolymarketClient] Failed to initialize Polymarket Client:', error);
      } finally {
        setIsInitializing(false);
      }
    }

    initClient();
  }, [
    ready,
    authenticated,
    wallets,
    accessToken,
    user?.walletAddress,
    client,
    lastInitializedAddress,
  ]);

  return {
    client,
    signer,
    isInitializing,
  };
}
